import { MapViewerEventObject, MapViewerInputNode, MapViewerNode } from '@3ddv/dvm-internal';
import { MapViewerComponent, MapViewerEvent } from '@3ddv/ngx-dvm-internal';
import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, catchError, of, take, takeWhile } from 'rxjs';
import { EventsTicketsService } from '../../../events/data-access/events-tickets.service';
import { EventsService } from '../../../events/data-access/events.service';
import { PanoramicViewerService } from '../../../seat-selection/data-access/panoramic-viewer.service';
import { SeatSelectionState } from '../../../seat-selection/data-access/seat-selection.state';
import { SectionsAPI } from '../_sections.api';
import { ComputedAvailability, ExchangeService } from '../exchange.service';
import { ModalService } from '../modal.service';
import { SeatsService } from '../seats.service';
import { SectionsService } from '../sections.service';
import { TimerService } from '../timer.service';
import { SectionViewerService } from './section-viewer.service';

@Injectable({
  providedIn: 'root',
})
export class SeatViewerService {
  private _seatsService = inject(SeatsService);
  private _eventTicketsService = inject(EventsTicketsService);
  private _modalService = inject(ModalService);
  private _ngZone = inject(NgZone);
  private _exchangeService = inject(ExchangeService);
  private _sectionsApi = inject(SectionsAPI);
  private _eventService = inject(EventsService);
  private _seatSelectionState = inject(SeatSelectionState);
  private _timerService = inject(TimerService);
  private _sectionViewerService = inject(SectionViewerService);
  private _sectionsService = inject(SectionsService);
  private _panoramicViewerService = inject(PanoramicViewerService);

  public readonly _isSeatMapVisible = new BehaviorSubject(false);
  public readonly isSeatMapVisible$ = this._isSeatMapVisible.asObservable();
  public sectionWithSeatsSelected: string | null = null;

  private _hasLoadFailed = new Subject<boolean>();
  public hasLoadFailed$ = this._hasLoadFailed.asObservable();

  private readonly subscriptions: Subscription[] = [];

  public getIsSeatMapVisible() {
    return this._isSeatMapVisible.getValue();
  }

  /**
   * Cleans up and unsubscribes from all subscriptions when the viewer is no longer needed to avoid memory leaks.
   */
  public cleanSubscriptions = () => {
    this.subscriptions.forEach(s => s.unsubscribe());
  };

  /**
   * Updates the load failure status for the seat map.
   *
   * @param value - Indicates whether the seat map load has failed.
   */
  public setHasLoadFailed = (value: boolean) => {
    this._hasLoadFailed.next(value);
  };

  /**
   * Prepares the seat map for a specific section, sets up necessary subscriptions, and updates the state accordingly.
   *
   * @param viewer - The instance of the viewer component to display the seat map.
   * @param node - The node representing the section in the viewer.
   */
  private setupSeatMap = (viewer: MapViewerComponent, node: MapViewerNode) => {
    this.cleanSubscriptions();
    this._ngZone.run(() => {
      this.subscribeToLoadSeatMap(viewer, node);
      this.subscribeToLoadSeatMapSuccess(viewer, node.id);

      const loadFailSubscription = this.hasLoadFailed$.subscribe(loadFailed => {
        if (!loadFailed) {
          this._isSeatMapVisible.next(true);

          this.subscribeToSeatClick(viewer);

          const mapLoadingSubscription = this._sectionViewerService.isMapLoading$
            .pipe(takeWhile(isMapLoading => isMapLoading, true))
            .subscribe(isMapLoading => {
              this._isSeatMapVisible.next(true);

              if (!isMapLoading) {
                this._sectionsService.selectSection(node);
                if (node.id === this.sectionWithSeatsSelected) {
                  this.selectSeatsOnMap();
                } else {
                  this._seatsService.resetSeatSelectedData();
                }
              }
            });
          this.subscriptions.push(mapLoadingSubscription);
        }
      });
      this.subscriptions.push(loadFailSubscription);
    });
  };

  /**
   * Handles user interaction with the seat map, enabling section selection and resetting selections if needed.
   *
   * @param node - The node representing the clicked section in the seat map.
   * @param viewer - The viewer component instance.
   */
  public handleClickOnSeatMapEnabled(node: MapViewerNode, viewer: MapViewerComponent) {
    if (!node?.id || node.type !== 'section' || this.getIsSeatMapVisible()) return;
    this._sectionViewerService.cleanSubscriptions();

    if (this.sectionWithSeatsSelected && node.id !== this.sectionWithSeatsSelected) {
      this._ngZone.run(() => {
        this._seatsService.resetSeatSelectedData();
        this._sectionViewerService.unselectAllMapSections();
        this.sectionWithSeatsSelected = null;
      });
    }

    const isFromPanoViewer = this._seatSelectionState.getState() === '3d-view';

    if (isFromPanoViewer) {
      this._panoramicViewerService.cleanSubscriptions();
    }

    if (node.state === 'available' || node.state === 'selected' || isFromPanoViewer) {
      this._ngZone.run(() => {
        this.setupSeatMap(viewer, node);
      });
    }
  }

  /**
   * Switches to the section view and resets state accordingly.
   */
  public goToSectionView = () => {
    if (this._sectionViewerService.viewer2DRef) {
      this.cleanSubscriptions();
      this._sectionViewerService.cleanSubscriptions();
      this._seatSelectionState.resetState();
      this._sectionViewerService.setupViewer(this._sectionViewerService.viewer2DRef);
      this._isSeatMapVisible.next(false);
    }
  };

  /**
   * Configures the viewer flags and scaling factors for optimal user experience.
   * @param viewer The MapViewerComponent instance being configured.
   */
  private configureSeatMapFlags = (viewer: MapViewerComponent) => {
    viewer.service.flags.fixed_aspect_ratio = false;
    viewer.service.min_scaling_factor = 1;
    viewer.service.max_scaling_factor = 100;
    const selectedTickets = this._eventTicketsService.getSelectedTicketsData()?.length!;
    if (selectedTickets) {
      viewer.service.max_selection = selectedTickets;
    }
  };

  /**
   * Subscribes to the seat map loading process and handles errors if the load fails.
   *
   * @param viewer - The viewer component instance.
   * @param section - The node representing the section to load.
   */
  private subscribeToLoadSeatMap = (viewer: MapViewerComponent, section: MapViewerNode) => {
    const mapData = this._sectionViewerService.getMapData();
    if (!mapData) throw new Error('Map data was incorrect or not provided');

    const subscription = viewer.service
      .loadMap({ venue_id: mapData.venue_id, map_id: section.id })
      .pipe(
        catchError(error => {
          this.onSeatMapProviderError();
          this.setHasLoadFailed(true);
          return of(false);
        })
      )
      .subscribe(isLoaded => {
        if (!isLoaded) return;
        this.setHasLoadFailed(false);

        if (this._timerService.isCheckoutCanceled) {
          this._seatSelectionState.setSelectState();
          this._sectionViewerService.unselectAllMapSections();

          const subscription = this._sectionsService.selectedSectionData$.pipe(take(1)).subscribe(section => {
            if (!section) return;

            this._sectionViewerService.viewer2DRef?.service.setAvailable('section', section.sectionID.toString());
            this._sectionViewerService.selectNode(section.sectionID.toString());
            this._timerService.isCheckoutCanceled = false;
          });

          this.subscriptions.push(subscription);
        }
      });
    this.subscriptions.push(subscription);
  };

  /**
   * Subscribes to seat map load success events and fetches seat availability data when the map loads successfully.
   *
   * @param viewer - The viewer component instance.
   * @param sectionId - The ID of the section being loaded.
   */
  private subscribeToLoadSeatMapSuccess = (viewer: MapViewerComponent, sectionId: string) => {
    this._sectionViewerService.setIsMapLoading(true);

    const subscription = viewer.service
      .getObservable('load_success')
      .pipe(catchError(this.onSeatMapProviderError))
      .subscribe(isLoaded => {
        if (!isLoaded) return;
        this.configureSeatMapFlags(viewer);
        this.fetchAndSetSeatAvailability(sectionId);
      });
    this.subscriptions.push(subscription);
  };

  /**
   * Subscribes to seat click events on the seat map and handles seat selection and deselection.
   *
   * @param viewer - The viewer component instance.
   */
  private subscribeToSeatClick = (viewer: MapViewerComponent) => {
    const subscription = viewer.service
      .getObservable('click')
      .subscribe(viewerEvent => this.handleSeatMapClick(viewerEvent as MapViewerEvent<MapViewerEventObject>));
    this.subscriptions.push(subscription);
  };

  /**
   * Handles seat click events on the seat map, selecting or deselecting seats based on their state.
   *
   * @param viewerEvent - The event object triggered by clicking on the seat map.
   */
  private handleSeatMapClick = (viewerEvent: MapViewerEvent<MapViewerEventObject>) => {
    const selectedSeats = this._seatsService._selectedSeats.getValue();
    const selectedTickets = this._eventTicketsService.getSelectedTicketsData()?.length;
    const [node] = viewerEvent.event.nodes;

    if (selectedSeats?.length === selectedTickets && node?.state === 'available') {
      return;
    }

    if (!node?.id || node.type !== 'seat' || node.state === 'unavailable') return;

    const seatId = node.id.substring(node.id.lastIndexOf('-') + 1, node.id.length);
    const seatData = this._seatsService.getSeatData(+seatId, this._eventService.getEventConfiguration()!.buyerType)!;
    const currentSection = this._sectionsService.getSelectedSectionData();

    if (currentSection) {
      seatData.seatData.section.description =
        currentSection.sectionShortPublicDescription ||
        currentSection.sectionPublicDescription ||
        currentSection.sectionDescription;
    }

    if (node.state === 'selected') {
      this._seatsService.unselectSeatFromArray(seatData);
      const updatedSelectedSeats = this._seatsService.getSelectedSeatsData();
      const sectionHasSeatsSelected = updatedSelectedSeats?.some(seat => seat.seatData.section.refKey === node.parent);
      if (!sectionHasSeatsSelected) {
        this._sectionViewerService.viewer2DRef?.service.unselect([node.parent ?? '']);
      }
      return;
    }

    if (seatData) {
      const updatedSelectedSeats = this._seatsService.getSelectedSeatsData();
      const isSelected = updatedSelectedSeats?.some(seat => seat.seatData.id === seatData.seatData.id);
      if (isSelected) return;

      this._seatsService.setSelectedSeatToArray(seatData);
      this._sectionViewerService.selectNode(node.parent ?? '');

      if (node.parent) {
        this.sectionWithSeatsSelected = node.parent;
      }
    }
  };

  /**
   * Sets the seat availability in the viewer for a specific section.
   *
   * @param computedAvailability - The computed availability data for the seats.
   * @param sectionId - The ID of the section being updated.
   */
  private setSeatMapAvailability = (computedAvailability: ComputedAvailability[], sectionId: MapViewerInputNode) => {
    const finalSeatIds = computedAvailability.map(seat => {
      return `${sectionId}-${seat.seatId}`;
    });

    return this._sectionViewerService.viewer2DRef?.service.setAvailability('seat', finalSeatIds, sectionId);
  };

  /**
   * Fetches seat availability for a section and sets the data in the viewer.
   *
   * @param sectionId - The ID of the section for which to fetch availability.
   */
  private fetchAndSetSeatAvailability = (sectionId: string) => {
    const selectedEventId = this._eventService?.selectedEvent?.id;
    const selectedTickets = this._eventTicketsService.getSelectedTicketsData();
    return this._sectionsApi
      .fetchSeatAvailavility(selectedEventId!, +sectionId, selectedTickets)
      .pipe(take(1), catchError(this._sectionViewerService.onAvailabilityFetchError))
      .subscribe(availabilityData => {
        if (!availabilityData) return;

        const availableSeatsData = {
          availableSeats: availabilityData.availableSeats,
          pricePoints: availabilityData.pricePoints,
        };

        this._seatsService.setAvailableSeats(availableSeatsData);

        if (!availabilityData.availableSeats.length) {
          const config = {
            type: 'error',
            text: 'No valid exchangeable tickets found for this section.',
            buttonText: 'Close',
            buttonAction: () => {
              this._modalService.closeModal();
            },
          };
          this._modalService.openModal(config);

          this.goToSectionView();
          return;
        }

        const computedAvailability = this._exchangeService.getComputedAvailabilityFromSeatAvailability(
          availabilityData.availableSeats,
          availabilityData.reference
        );

        const isAvailabilitySet = this.setSeatMapAvailability(computedAvailability, sectionId);
        this.selectSeatsOnMap();

        if (isAvailabilitySet) {
          this._sectionViewerService.setIsMapLoading(false);
        }
      });
  };

  /**
   * Selects seats on the map based on currently selected seats in the state.
   */
  private selectSeatsOnMap = () => {
    const rawSelectedSeats = this._seatsService.getSelectedSeats();
    return this._sectionViewerService.viewer2DRef?.service.select(rawSelectedSeats);
  };

  /**
   * Handles errors from the seat map provider and shows a modal with an error message.
   */
  private onSeatMapProviderError = async () => {
    const config = {
      type: 'app',
      text: 'The seat provider is currently unavailable due to maintenance. Please try again later.',
      buttonText: 'Close',
      buttonAction: () => {
        this._modalService.closeModal();
      },
    };

    this.goToSectionView();
    this._modalService.openModal(config);
    return of(undefined);
  };
}
